import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  RotateCcw,
  Trash2,
  Undo2
} from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Textarea } from '../ui/textarea'
import { Button } from '../ui/button'
import { useToast } from '../ui/toast'
import { PlanConfigFormFields } from './PlanConfigFormFields'
import { useMeasurements } from '../../hooks/useMeasurements'
import {
  DEFAULT_WEEK_DISTRIBUTION_CURVE,
  getActiveConfig,
  getAllConfigs,
  createConfig,
  activateConfig,
  deleteConfig,
  withPlanConfigDefaults,
  type PlanGenerationConfigDto
} from '../../lib/api/planGeneration'
import { formatMeasurement, type MeasurementKind, type MeasurementUnit } from '../../lib/measurements'

/** Auto-generated version label for snapshots, e.g. "2026-06-09 21:14" (backend allows max 20 chars). */
const formatVersionTimestamp = (date: Date): string => {
  const pad = (n: number) => `${n}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const extractApiError = (err: unknown, fallback: string): string =>
  err && typeof err === 'object' && 'response' in err
    ? (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? fallback
    : fallback

/** Serializes only the tunable fields so metadata (id/version/name/timestamps) never marks the form dirty. */
const comparableJson = (config: PlanGenerationConfigDto): string => {
  const clone: Record<string, unknown> = { ...config }
  delete clone.id
  delete clone.version
  delete clone.name
  delete clone.isActive
  delete clone.createdAt
  delete clone.updatedAt
  delete clone.createdBy
  return JSON.stringify(clone)
}

type PlanConfigFieldDescriptor = {
  label: string
  select: (config: PlanGenerationConfigDto) => number | string | boolean | null | undefined
  format: (value: number | string | boolean | null | undefined, unit: MeasurementUnit) => string
}

export type RecentPlanConfigChange = {
  id?: string | null
  version: string
  isActive: boolean
  createdAt?: string | null
  createdBy?: string | null
  notes?: string | null
  comparedToVersion?: string | null
  settingChanges: string[]
  additionalSettingChangeCount: number
}

const RECENT_CHANGE_LIMIT = 3
const SETTING_CHANGE_LIMIT = 4

const asFiniteNumber = (value: number | string | boolean | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

const formatCompactNumber = (value: number, maximumFractionDigits = 2) =>
  value.toLocaleString('en-US', {
    maximumFractionDigits
  })

const formatConfigMeasurement =
  (kind: MeasurementKind) =>
    (value: number | string | boolean | null | undefined, unit: MeasurementUnit) =>
      formatMeasurement(asFiniteNumber(value), kind, unit)

const formatConfigPercent = (value: number | string | boolean | null | undefined) =>
  `${formatCompactNumber(asFiniteNumber(value) * 100, 1)}%`

const formatConfigCount = (value: number | string | boolean | null | undefined) =>
  formatCompactNumber(asFiniteNumber(value), 0)

const formatConfigNumber = (value: number | string | boolean | null | undefined) =>
  formatCompactNumber(asFiniteNumber(value), 2)

const PLAN_CONFIG_FIELD_DESCRIPTORS: PlanConfigFieldDescriptor[] = [
  { label: 'Min weekly distance', select: c => c.weeklyMinDistanceKm, format: formatConfigMeasurement('distance') },
  { label: 'Max weekly distance', select: c => c.weeklyMaxDistanceKm, format: formatConfigMeasurement('distance') },
  { label: 'Min weekly elevation', select: c => c.weeklyMinElevationGainMeters, format: formatConfigMeasurement('elevation') },
  { label: 'Max weekly elevation', select: c => c.weeklyMaxElevationGainMeters, format: formatConfigMeasurement('elevation') },
  { label: 'Max workout elevation', select: c => c.maxNonMilestoneWorkoutElevationMeters, format: formatConfigMeasurement('elevation') },
  { label: 'Protective max elevation', select: c => c.maxNonMilestoneWorkoutElevationMetersForProtectiveBias, format: formatConfigMeasurement('elevation') },
  { label: 'True elevation requirement (default)', select: c => c.trueElevationMinFractionDefault, format: formatConfigPercent },
  { label: 'True elevation requirement (knee)', select: c => c.trueElevationMinFractionKneeIssues, format: formatConfigPercent },
  { label: 'Pack workout fraction (default)', select: c => c.packWorkoutFractionDefault, format: formatConfigPercent },
  { label: 'Pack workout fraction (knee)', select: c => c.packWorkoutFractionKneeIssues, format: formatConfigPercent },
  { label: 'Treadmill max distance', select: c => c.treadmillMaxDistanceKm, format: formatConfigMeasurement('distance') },
  { label: 'Minimum workout days', select: c => c.minimumWorkoutDaysPerWeek, format: formatConfigCount },
  { label: 'Default duration', select: c => c.defaultPlanDurationWeeks, format: formatConfigCount },
  { label: 'Minimum recommended weeks', select: c => c.minimumRecommendedWeeks, format: formatConfigCount },
  { label: 'Top distance days', select: c => c.topDistanceDaysCount, format: formatConfigCount },
  { label: 'Top elevation days', select: c => c.topElevationDaysCount, format: formatConfigCount },
  { label: 'Cohort age threshold', select: c => c.cohortWeeklyMinimums.ageThresholdYears, format: formatConfigCount },
  { label: 'Under-40 min weekly distance', select: c => c.cohortWeeklyMinimums.under40MinWeeklyDistanceKm, format: formatConfigMeasurement('distance') },
  { label: 'Under-40 min weekly elevation', select: c => c.cohortWeeklyMinimums.under40MinWeeklyElevationMeters, format: formatConfigMeasurement('elevation') },
  { label: 'Over-40 min weekly distance', select: c => c.cohortWeeklyMinimums.over40MinWeeklyDistanceKm, format: formatConfigMeasurement('distance') },
  { label: 'Over-40 min weekly elevation', select: c => c.cohortWeeklyMinimums.over40MinWeeklyElevationMeters, format: formatConfigMeasurement('elevation') },
  { label: 'Level 1 distance multiplier', select: c => c.difficultyMultipliers.level1Distance, format: formatConfigNumber },
  { label: 'Level 1 elevation multiplier', select: c => c.difficultyMultipliers.level1Elevation, format: formatConfigNumber },
  { label: 'Level 2 distance multiplier', select: c => c.difficultyMultipliers.level2Distance, format: formatConfigNumber },
  { label: 'Level 2 elevation multiplier', select: c => c.difficultyMultipliers.level2Elevation, format: formatConfigNumber },
  { label: 'Level 3 distance multiplier', select: c => c.difficultyMultipliers.level3Distance, format: formatConfigNumber },
  { label: 'Level 3 elevation multiplier', select: c => c.difficultyMultipliers.level3Elevation, format: formatConfigNumber },
  { label: 'Level 4 distance multiplier', select: c => c.difficultyMultipliers.level4Distance, format: formatConfigNumber },
  { label: 'Level 4 elevation multiplier', select: c => c.difficultyMultipliers.level4Elevation, format: formatConfigNumber },
  { label: 'Milestone minimum week', select: c => c.milestoneMinimumWeek, format: formatConfigCount },
  { label: 'Two-workout split threshold', select: c => c.milestoneSplits.twoWorkoutMaxElevationMeters, format: formatConfigMeasurement('elevation') },
  { label: 'Three-workout split threshold', select: c => c.milestoneSplits.threeWorkoutMaxElevationMeters, format: formatConfigMeasurement('elevation') },
  { label: 'Milestone lead workout fraction', select: c => c.milestoneSplits.leadWorkoutFraction, format: formatConfigPercent },
  { label: 'Durability O40 weight', select: c => c.durabilityTtlWeights.o40Weight, format: formatConfigPercent },
  { label: 'Durability elevation weight', select: c => c.durabilityTtlWeights.elevationWeight, format: formatConfigPercent },
  { label: 'Durability pack weight', select: c => c.durabilityTtlWeights.packWeight, format: formatConfigPercent },
  { label: 'Default TTL max increase', select: c => c.durabilityWeekOverWeekCaps.defaultTtlMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Under-40 joint TTL max increase', select: c => c.durabilityWeekOverWeekCaps.under40JointTtlMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Age 40+ TTL max increase', select: c => c.durabilityWeekOverWeekCaps.age40PlusTtlMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Age 40+ joint TTL max increase', select: c => c.durabilityWeekOverWeekCaps.age40PlusJointTtlMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Elevation max increase', select: c => c.durabilityWeekOverWeekCaps.elevationMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Pack step max increase', select: c => c.durabilityWeekOverWeekCaps.packStepMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Elevation debt spike previous-week cap', select: c => c.durabilityWeekOverWeekCaps.elevationDebtSpikePreviousWeekMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Protective elevation debt previous-week cap', select: c => c.durabilityWeekOverWeekCaps.protectiveElevationDebtSpikePreviousWeekMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Elevation debt spike original-week cap', select: c => c.durabilityWeekOverWeekCaps.elevationDebtSpikeOriginalWeekMaxIncreaseFraction, format: formatConfigPercent },
  { label: 'Protective elevation debt original-week cap', select: c => c.durabilityWeekOverWeekCaps.protectiveElevationDebtSpikeOriginalWeekMaxIncreaseFraction, format: formatConfigPercent }
]

const valuesAreEqual = (a: unknown, b: unknown) => {
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 0.000001
  }
  return Object.is(a, b)
}

const describeFieldChange = (
  descriptor: PlanConfigFieldDescriptor,
  current: PlanGenerationConfigDto,
  previous: PlanGenerationConfigDto,
  unit: MeasurementUnit
) => {
  const currentValue = descriptor.select(current)
  const previousValue = descriptor.select(previous)
  if (valuesAreEqual(currentValue, previousValue)) return null
  return `${descriptor.label}: ${descriptor.format(previousValue, unit)} to ${descriptor.format(currentValue, unit)}`
}

const describeWeekDistributionCurveChange = (current: PlanGenerationConfigDto, previous: PlanGenerationConfigDto) => {
  const currentCurve = current.weekDistributionCurve?.length ? current.weekDistributionCurve : DEFAULT_WEEK_DISTRIBUTION_CURVE
  const previousCurve = previous.weekDistributionCurve?.length ? previous.weekDistributionCurve : DEFAULT_WEEK_DISTRIBUTION_CURVE
  const maxLength = Math.max(currentCurve.length, previousCurve.length)
  const changedWeeks = Array.from({ length: maxLength }, (_, index) => ({
    index,
    current: currentCurve[index],
    previous: previousCurve[index]
  })).filter(({ current, previous }) => !valuesAreEqual(current, previous))

  if (changedWeeks.length === 0) return null
  if (changedWeeks.length <= 3) {
    const changes = changedWeeks.map(({ index, current, previous }) =>
      `week ${index + 1} ${formatCompactNumber(previous ?? 0, 2)} to ${formatCompactNumber(current ?? 0, 2)}`
    )
    return `Week distribution curve: ${changes.join(', ')}`
  }

  return `Week distribution curve: ${changedWeeks.length} week values changed`
}

const describePlanConfigSettingChanges = (
  current: PlanGenerationConfigDto,
  previous: PlanGenerationConfigDto,
  unit: MeasurementUnit
) => [
  describeWeekDistributionCurveChange(current, previous),
  ...PLAN_CONFIG_FIELD_DESCRIPTORS.map(descriptor => describeFieldChange(descriptor, current, previous, unit))
].filter((change): change is string => Boolean(change))

const parseSnapshotDate = (value?: string | null) => {
  if (!value) return 0
  const normalized = value.includes(' ') ? value.replace(' ', 'T') : value
  const timestamp = Date.parse(normalized)
  return Number.isFinite(timestamp) ? timestamp : 0
}

const snapshotTimestamp = (config: PlanGenerationConfigDto) =>
  parseSnapshotDate(config.createdAt) || parseSnapshotDate(config.updatedAt) || parseSnapshotDate(config.version)

const sortSnapshotsByCreatedAtDesc = (configs: PlanGenerationConfigDto[]) =>
  [...configs].sort((a, b) =>
    snapshotTimestamp(b) - snapshotTimestamp(a) || (b.version ?? '').localeCompare(a.version ?? '')
  )

export const buildRecentPlanConfigChanges = (
  configs: PlanGenerationConfigDto[],
  unit: MeasurementUnit = 'Metric',
  limit = RECENT_CHANGE_LIMIT
): RecentPlanConfigChange[] => {
  const sorted = sortSnapshotsByCreatedAtDesc(configs.map(config => withPlanConfigDefaults(config)))
  const changes: RecentPlanConfigChange[] = []

  for (let i = 0; i < sorted.length && changes.length < limit; i += 1) {
    const current = sorted[i]
    const previous = sorted[i + 1]
    const settingChanges = previous ? describePlanConfigSettingChanges(current, previous, unit) : []
    const notes = current.notes?.trim() || null

    if (!notes && settingChanges.length === 0) continue

    changes.push({
      id: current.id,
      version: current.version,
      isActive: current.isActive,
      createdAt: current.createdAt,
      createdBy: current.createdBy,
      notes,
      comparedToVersion: previous?.version ?? null,
      settingChanges: settingChanges.slice(0, SETTING_CHANGE_LIMIT),
      additionalSettingChangeCount: Math.max(0, settingChanges.length - SETTING_CHANGE_LIMIT)
    })
  }

  return changes
}

const formatSnapshotDateLabel = (value?: string | null) => {
  const timestamp = parseSnapshotDate(value)
  if (!timestamp) return null
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(timestamp))
}

export const PlanGenerationConfigPanel = () => {
  const { addToast } = useToast()
  const measurements = useMeasurements()

  // The single always-visible form state, plus the last-loaded snapshot for dirty tracking
  const [editingConfig, setEditingConfig] = useState<PlanGenerationConfigDto | null>(null)
  const [savedConfig, setSavedConfig] = useState<PlanGenerationConfigDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [recentChangesOpen, setRecentChangesOpen] = useState(true)

  // Action states
  const [saving, setSaving] = useState(false)
  const [reloading, setReloading] = useState(false)

  // Version history (collapsed by default)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [history, setHistory] = useState<PlanGenerationConfigDto[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const isDirty = useMemo(
    () =>
      editingConfig != null &&
      savedConfig != null &&
      comparableJson(editingConfig) !== comparableJson(savedConfig),
    [editingConfig, savedConfig]
  )

  /** Normalizes a loaded config into the form and resets dirty tracking. */
  const applyLoadedConfig = useCallback((config: PlanGenerationConfigDto) => {
    const normalized = { ...withPlanConfigDefaults(config), notes: '' }
    setEditingConfig(normalized)
    setSavedConfig(normalized)
  }, [])

  // Load the active config. The server already returns defaults when nothing is active, so a failed
  // read must surface as an error: loading defaults here would let Save replace the live settings (BH-19).
  const loadActiveConfig = useCallback(async () => {
    try {
      applyLoadedConfig(await getActiveConfig())
      setError(null)
    } catch (err) {
      console.error('Failed to load plan generation settings', err)
      setError('Failed to load the plan generation settings')
    }
  }, [applyLoadedConfig])

  useEffect(() => {
    setLoading(true)
    loadActiveConfig().finally(() => setLoading(false))
  }, [loadActiveConfig])

  const loadHistory = useCallback(async (options?: { quiet?: boolean }) => {
    setHistoryLoading(true)
    try {
      const all = await getAllConfigs()
      setHistory(all.map(config => withPlanConfigDefaults(config)))
      setHistoryError(null)
    } catch (err) {
      console.error('Failed to load version history', err)
      setHistoryError('Failed to load recent setting changes')
      if (!options?.quiet) {
        addToast({ title: 'Failed to load version history', variant: 'error' })
      }
    } finally {
      setHistoryLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    void loadHistory({ quiet: true })
  }, [loadHistory])

  useEffect(() => {
    if (historyOpen) void loadHistory()
  }, [historyOpen, loadHistory])

  const sortedHistory = useMemo(() => {
    const ts = (c: PlanGenerationConfigDto) => (c.createdAt ? new Date(c.createdAt).getTime() : 0)
    const active = history.filter(c => c.isActive)
    const rest = history
      .filter(c => !c.isActive)
      .sort((a, b) => ts(b) - ts(a) || (b.version ?? '').localeCompare(a.version ?? ''))
    return [...active, ...rest]
  }, [history])

  const recentChanges = useMemo(
    () => buildRecentPlanConfigChanges(history, measurements.unit),
    [history, measurements.unit]
  )

  /**
   * Save = create a NEW config snapshot, then activate it. The previously active
   * config is kept untouched, so every save silently adds a restorable version.
   */
  const handleSave = async () => {
    if (!editingConfig || !isDirty || saving) return

    setSaving(true)

    let created: PlanGenerationConfigDto
    try {
      created = await createConfig({
        ...editingConfig,
        id: undefined,
        version: formatVersionTimestamp(new Date()),
        name: 'Algorithm settings',
        isActive: false,
        notes: editingConfig.notes?.trim() ? editingConfig.notes.trim() : null
      })
    } catch (err) {
      addToast({
        title: 'Failed to save settings',
        description: extractApiError(err, 'Your changes were not saved. Please try again.'),
        variant: 'error'
      })
      setSaving(false)
      return
    }

    try {
      if (!created.id) throw new Error('Create response did not include an id')
      const activated = await activateConfig(created.id)
      applyLoadedConfig(activated)
      addToast({
        title: 'Settings saved',
        description: 'Newly generated plans will use these settings. A snapshot was added to Version history.',
        variant: 'success'
      })
    } catch (err) {
      console.error('Failed to activate new config', err)
      addToast({
        title: 'Save incomplete',
        description: 'A snapshot was created but could not be activated, so the previous settings are still live. Try restoring it from Version history.',
        variant: 'error'
      })
      await loadActiveConfig()
    } finally {
      setSaving(false)
      void loadHistory({ quiet: !historyOpen })
    }
  }

  const handleDiscard = async () => {
    setReloading(true)
    await loadActiveConfig()
    setReloading(false)
  }

  const handleRestore = async (config: PlanGenerationConfigDto) => {
    if (!config.id) return
    const confirmed = window.confirm(
      isDirty
        ? `Restore version "${config.version}"? It becomes the active settings for new plans, and your unsaved edits will be discarded.`
        : `Restore version "${config.version}"? It becomes the active settings for new plans.`
    )
    if (!confirmed) return

    setRestoringId(config.id)
    try {
      const activated = await activateConfig(config.id)
      applyLoadedConfig(activated)
      addToast({
        title: 'Version restored',
        description: `"${config.version}" is now active for newly generated plans.`,
        variant: 'success'
      })
      await loadHistory()
    } catch (err) {
      addToast({
        title: 'Failed to restore version',
        description: extractApiError(err, 'The version could not be activated.'),
        variant: 'error'
      })
    } finally {
      setRestoringId(null)
    }
  }

  const handleDelete = async (config: PlanGenerationConfigDto) => {
    if (!config.id) return
    if (!window.confirm(`Delete snapshot "${config.version}"? This cannot be undone.`)) return

    setDeletingId(config.id)
    try {
      await deleteConfig(config.id)
      setHistory(prev => prev.filter(c => c.id !== config.id))
    } catch (err) {
      addToast({
        title: 'Failed to delete snapshot',
        description: extractApiError(err, 'The snapshot could not be deleted.'),
        variant: 'error'
      })
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading settings...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold text-foreground">Generation Config</h2>
        <p className="text-sm text-muted-foreground">
          These settings apply to all newly generated training plans. Every save keeps a restorable
          snapshot in Version history below.
        </p>
        <p className="max-w-3xl text-xs leading-relaxed text-muted-foreground">
          Plans are generated in stages: (1) the goal hike&apos;s hardest days are multiplied into total plan volume
          using the difficulty multipliers; (2) that volume is spread across weeks using the distribution curve;
          (3) weekly minimums/maximums and age-cohort floors are applied; (4) durability guardrails smooth
          the build and track repayable debt; (5) workouts are assembled per day and milestone workouts are layered on near the end of the
          plan. Each section below controls one of those stages.
        </p>
      </header>

      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-2 py-4 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => {
                setLoading(true)
                loadActiveConfig().finally(() => setLoading(false))
              }}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-primary/10 bg-primary/5">
        <CardHeader className={recentChangesOpen ? 'pb-3' : 'pb-4'}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-base">
                <button
                  type="button"
                  className="flex items-center gap-2 text-left transition-colors hover:text-primary"
                  aria-expanded={recentChangesOpen}
                  aria-controls="recent-setting-changes-content"
                  onClick={() => setRecentChangesOpen(open => !open)}
                >
                  {recentChangesOpen ? (
                    <ChevronDown className="h-4 w-4 text-primary" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-primary" aria-hidden="true" />
                  )}
                  <History className="h-4 w-4 text-primary" aria-hidden="true" />
                  <span>Recent setting changes</span>
                </button>
              </CardTitle>
              <CardDescription>
                Snapshot notes and field changes from the newest saved algorithm settings.
              </CardDescription>
            </div>
            {historyLoading && history.length > 0 && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Refreshing
              </span>
            )}
          </div>
        </CardHeader>
        {recentChangesOpen && (
          <CardContent id="recent-setting-changes-content" className="space-y-3">
            {historyLoading && history.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Loading recent changes...
              </div>
            ) : historyError ? (
              <p className="text-xs text-destructive">{historyError}</p>
            ) : recentChanges.length === 0 ? (
              <p className="text-xs leading-5 text-muted-foreground">
                No snapshot notes or comparable setting changes yet. Add a short note before saving so the next
                version answers what changed at a glance.
              </p>
            ) : (
              <div className="space-y-2">
                {recentChanges.map(change => {
                  const dateLabel = formatSnapshotDateLabel(change.createdAt ?? change.version)
                  return (
                    <article
                      key={change.id ?? change.version}
                      className="rounded-md border border-border/70 bg-background/80 px-3 py-2"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-foreground">{change.version}</span>
                        {change.isActive && (
                          <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                            Current
                          </span>
                        )}
                        {dateLabel && (
                          <span className="text-[10px] text-muted-foreground">{dateLabel}</span>
                        )}
                        {change.createdBy && (
                          <span className="text-[10px] text-muted-foreground">by {change.createdBy}</span>
                        )}
                      </div>
                      {change.notes ? (
                        <p className="mt-1 text-sm leading-5 text-foreground">{change.notes}</p>
                      ) : change.settingChanges.length > 0 ? (
                        <p className="mt-1 text-xs text-muted-foreground">No note saved with this snapshot.</p>
                      ) : null}
                      {change.settingChanges.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs leading-5 text-muted-foreground">
                          {change.settingChanges.map(settingChange => (
                            <li key={settingChange} className="flex gap-2">
                              <span className="mt-[0.55rem] h-1 w-1 shrink-0 rounded-full bg-primary/70" />
                              <span>{settingChange}</span>
                            </li>
                          ))}
                          {change.additionalSettingChangeCount > 0 && (
                            <li className="pl-3 text-[11px] text-muted-foreground">
                              +{change.additionalSettingChangeCount} more setting changes
                            </li>
                          )}
                        </ul>
                      )}
                      {change.comparedToVersion && (
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Compared with {change.comparedToVersion}
                        </p>
                      )}
                    </article>
                  )
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {editingConfig && (
        <Card>
          <CardHeader>
            <CardTitle>Algorithm settings</CardTitle>
            <CardDescription>
              Changes take effect for newly generated plans once you press Save.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Locked while saving, so the saved settings and note are what's on screen (BH-14). */}
            <fieldset disabled={saving} className="min-w-0 space-y-4">
              <PlanConfigFormFields
                value={editingConfig}
                onChange={setEditingConfig}
                showExplainers
              />

              <label className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">What changed? (optional)</span>
                <Textarea
                  value={editingConfig.notes ?? ''}
                  onChange={e => setEditingConfig(c => c ? { ...c, notes: e.target.value } : c)}
                  placeholder="Briefly describe what you changed — saved with the version snapshot..."
                  rows={2}
                />
              </label>
            </fieldset>

            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={handleSave} disabled={!isDirty || saving || reloading}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                Save
              </Button>
              <Button variant="outline" onClick={handleDiscard} disabled={!isDirty || saving || reloading}>
                {reloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
                Discard changes
              </Button>
              {isDirty && !saving && (
                <span className="text-xs text-muted-foreground">Unsaved changes</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Version history — collapsed by default; snapshots are created automatically on every save */}
      <div className="pt-2">
        <button
          type="button"
          onClick={() => setHistoryOpen(open => !open)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          {historyOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <History className="h-3.5 w-3.5" />
          Version history
        </button>

        {historyOpen && (
          <div className="mt-2 space-y-1.5">
            {historyLoading ? (
              <div className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading versions...
              </div>
            ) : sortedHistory.length === 0 ? (
              <p className="px-1 py-2 text-xs text-muted-foreground">
                No saved versions yet. A snapshot is added every time you save.
              </p>
            ) : (
              sortedHistory.map(config => (
                <div
                  key={config.id ?? config.version}
                  className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/30 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-foreground">{config.version}</span>
                      {config.isActive && (
                        <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                          Current
                        </span>
                      )}
                      {config.createdBy && (
                        <span className="text-[10px] text-muted-foreground">by {config.createdBy}</span>
                      )}
                    </div>
                    {config.notes && (
                      <p className="truncate text-[11px] text-muted-foreground">{config.notes}</p>
                    )}
                  </div>
                  {!config.isActive && config.id && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleRestore(config)}
                        disabled={restoringId === config.id || deletingId === config.id || saving}
                      >
                        {restoringId === config.id ? (
                          <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                        ) : (
                          <RotateCcw className="mr-1.5 h-3 w-3" />
                        )}
                        Restore
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 border-red-200 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleDelete(config)}
                        disabled={deletingId === config.id || restoringId === config.id}
                      >
                        {deletingId === config.id ? (
                          <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="mr-1.5 h-3 w-3" />
                        )}
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
